import {
  AfterViewInit, ChangeDetectorRef, Component, ComponentRef, Input, OnChanges, OnDestroy, Type, ViewChild, ViewContainerRef
} from '@angular/core';
import { RootDynamicComponentHelper } from '@shared/helpers/root-dynamic-component-helper';
import { XatBotAgentType, XatBotConfig } from '@features/xatbot/models/xat-bot-config.model';
import { OverlayContainer } from '@angular/cdk/overlay';
import { Dialog } from '@angular/cdk/dialog';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from '@shared/services/dialog-service';
import { chatMountAction } from '../../../services/run-thread';
import { RunChatOverlayContainer } from './run-chat-overlay-container';
import { RunChatDialogService } from './run-chat-dialog.service';

/**
 * THE RUN THREAD, BESIDE ITS RUN (XAT-E-377) — the XatBot conversation a chat-started Xact run came from, shown where
 * the run is shown: the Action Monitor's Chat tab, the run window every user gets (XAT-E-377-05). The person reads the
 * agent and answers it while the script drives the page.
 *
 * A GUEST COPY of the thread. The chat the run came from may still be on screen, so this one keeps its own state and
 * never takes the input pointer or a `?xatThreadId=` hand-off (`coexistsWithPanel`, see keepsOwnChatState), and it
 * leaves the thread's legacy click and route actions to that chat (`actsOnThreadActions`).
 *
 * MOUNTED BY NAME, as the XatBot panel mounts its chats (`platform-floating-xatbot`): `XatbotModule` is lazy and this
 * component is eager. A chat started this run, so the module is loaded; if it somehow is not, the region says so.
 *
 * The chat follows its inputs (`chatMountAction`): the same thread keeps the conversation as it is, whatever the run
 * does — a segment, the settle, the next run of the same thread — and another thread or agent swaps it.
 */
@Component({
  standalone: false,
  selector: 'app-run-thread-chat',
  templateUrl: './run-thread-chat.component.html',
  styleUrls: ['./run-thread-chat.component.scss'],
  // The chat's menus, tooltips and dialogs draw above the monitor (XAT-E-617-01), not under it in the app's overlay
  // container. Its dialog services are its own so they open in that layer; each reports to the app's copy above it.
  providers: [
    { provide: OverlayContainer, useClass: RunChatOverlayContainer },
    Dialog,
    MatDialog,
    { provide: DialogService, useClass: RunChatDialogService }
  ]
})
export class RunThreadChatComponent implements AfterViewInit, OnChanges, OnDestroy {
  /** The run thread (`ActionMonitorRun.chatThreadId`). */
  @Input() threadId: string | null | undefined = null;

  /** The agent the thread belongs to (`ActionMonitorRun.chatBinding`), so a reply reaches the same agent. */
  @Input() agentType: string | null | undefined = null;

  /** The chat could not be created here — XatBot is not loaded in this tab. */
  unavailable = false;

  @ViewChild('chat', { read: ViewContainerRef, static: true }) private host!: ViewContainerRef;

  private chatRef: ComponentRef<any> | null = null;
  /** What the mounted chat was opened on — the thread, and the agent it talks to. */
  private mountedKey: string | null = null;
  private viewReady = false;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    // This component's own (its providers): what the chat opens, and the layer it opens it in (XAT-E-617-01).
    private readonly dialogs: MatDialog,
    private readonly overlays: OverlayContainer
  ) {}

  ngAfterViewInit(): void {
    this.viewReady = true;
    // Out of this check pass: creating a component while the view is being checked would change it mid-check.
    setTimeout(() => this.sync());
  }

  ngOnChanges(): void {
    if (this.viewReady) setTimeout(() => this.sync());
  }

  ngOnDestroy(): void {
    this.closeOwnDialogs();
    this.unmount();
  }

  /**
   * A DIALOG THE CHAT OPENED GOES WITH IT (XAT-E-617-01). Its layer is removed with this component, but the dialog is
   * listed with the app's (a child `MatDialog` reports to its parent) and holds the page's scroll lock — closed here,
   * not left open in a layer that is gone. The menus and tooltips close with the triggers that own them.
   */
  private closeOwnDialogs(): void {
    const layer = this.overlays as RunChatOverlayContainer;
    this.dialogs.openDialogs
      .filter(dialog => layer.holds(document.getElementById(dialog.id)))
      .forEach(dialog => dialog.close());
  }

  /** Mount, keep, swap or remove the chat so it shows `threadId` on `agentType`. */
  private sync(): void {
    const threadId = this.threadId?.trim() || null;
    const key = threadId ? `${threadId}|${this.agentType ?? ''}` : null;
    const action = chatMountAction(this.chatRef ? this.mountedKey : null, key);
    if (action === 'keep' || action === 'none') return;

    this.unmount();
    if (action === 'remove' || !threadId) return;

    const chatType = RootDynamicComponentHelper.getComponent('XatBotComponent') as Type<any> | undefined;
    if (!chatType) {
      this.unavailable = true;
      this.cdr.detectChanges();
      return;
    }

    this.chatRef = this.host.createComponent(chatType);
    const chat = this.chatRef.instance;
    const agentType = (this.agentType || undefined) as XatBotAgentType | undefined;
    chat.config = XatBotConfig.embedded({
      threadId,
      ...(agentType ? { agentType, threadFilterAgentNames: [agentType] } : {}),
      // THE PANEL'S PRESENTATION (review, 2026-09-27), as the XatBot panel lays it over its page-agent tab: the question as
      // the reader's bubble and then the answer — no thread-title bar with its new-thread button, no question repeated as
      // a heading, no Chat / Sources / Verification strip, no disclaimer under the composer. The window's own tabs
      // already say this is the conversation.
      showThreadTitleHeader: false,
      showNewThreadButton: false,
      hideQuestionHeader: true,
      hideAnswerTabs: true,
      showFooterText: false
    });
    // And kept: opening a Standard thread re-applies the research tab's chrome, which would put these back.
    chat.chromeOverrides = {
      showFooterText: false,
      showWhatsNewButton: false,
      showQuickViewBubbles: false,
      showQuickViewHint: false
    };
    // Set before the first detectChanges runs the chat's ngOnInit, which is where the guards read them.
    chat.coexistsWithPanel = true;
    chat.actsOnThreadActions = false;
    this.mountedKey = key;
    this.cdr.detectChanges();

    // DEFERRED TO A MACROTASK, as the thread rail does: before the chat's ngOnInit has run, onThreadChange falls back
    // to router navigation and would move the whole page.
    const opening = this.chatRef;
    setTimeout(() => {
      if (this.chatRef === opening) opening.instance.onThreadChange({ id: threadId });
    });
  }

  private unmount(): void {
    this.chatRef?.destroy();
    this.chatRef = null;
    this.mountedKey = null;
    this.unavailable = false;
  }
}
